import React, { useMemo } from "react";
import type { AudioAsset, MusicConfig } from "../../api";
import { AudioLibrary } from "./AudioLibrary";

type Props = {
  value: MusicConfig | undefined;
  onChange: (next: MusicConfig | undefined) => void;
  /** Reel total length — drives the default music bar duration. */
  reelDurationSec: number;
  /** Current playhead — used as default fromSec when adding music. */
  playheadSec?: number;
};

const BEDS: { id: NonNullable<MusicConfig["bed"]>; label: string; mood: string }[] = [
  { id: "ambient", label: "Ambient", mood: "ruhig · tief · cinematic" },
  { id: "pulse",   label: "Pulse",   mood: "treibend · energetisch · modern" },
  { id: "riser",   label: "Riser",   mood: "spannungsaufbau · climax" },
];

const DEFAULT_VOLUME = 0.18;

export const MusicPanel: React.FC<Props> = ({
  value,
  onChange,
  reelDurationSec,
  playheadSec = 0,
}) => {
  const enabled = !!value && value.enabled;

  const sourceLabel = useMemo(() => {
    if (!value) return null;
    if (value.src) return value.label ?? "Custom Audio";
    if (value.bed) return BEDS.find((b) => b.id === value.bed)?.label ?? value.bed;
    return null;
  }, [value]);

  const setBed = (bed: NonNullable<MusicConfig["bed"]>): void => {
    onChange({
      bed,
      src: undefined,
      assetId: undefined,
      label: BEDS.find((b) => b.id === bed)?.label,
      volume: value?.volume ?? DEFAULT_VOLUME,
      enabled: true,
      fromSec: value?.fromSec ?? 0,
      durSec: value?.durSec,
      trimStartSec: value?.trimStartSec ?? 0,
      fadeInSec: value?.fadeInSec ?? 0,
      fadeOutSec: value?.fadeOutSec ?? 0,
      loop: true,
    });
  };

  const useAsset = (asset: AudioAsset): void => {
    const dur = asset.durationSec > 0 ? asset.durationSec : reelDurationSec;
    onChange({
      bed: undefined,
      src: asset.url,
      assetId: asset.id,
      label: asset.displayName,
      volume: value?.volume ?? 0.6,
      enabled: true,
      fromSec: value?.fromSec ?? 0,
      durSec: Math.min(dur, Math.max(reelDurationSec, dur)),
      trimStartSec: 0,
      fadeInSec: value?.fadeInSec ?? 0.4,
      fadeOutSec: value?.fadeOutSec ?? 0.8,
      loop: false,
    });
  };

  const patch = (p: Partial<MusicConfig>): void => {
    if (!value) return;
    onChange({ ...value, ...p });
  };

  const toggleEnabled = (): void => {
    if (!value) {
      onChange({ bed: "ambient", volume: DEFAULT_VOLUME, enabled: true, fromSec: 0, loop: true });
    } else {
      onChange({ ...value, enabled: !value.enabled });
    }
  };

  const clear = (): void => onChange(undefined);

  const isCustom = !!value?.src;
  const totalSec = Math.max(0.1, reelDurationSec);
  const fromSec = value?.fromSec ?? 0;
  const durSec = value?.durSec ?? Math.max(0.5, totalSec - fromSec);

  return (
    <div className="side-panel music-panel">
      <div className="side-panel-head">
        <h4>🎵 Background Music</h4>
        <button
          type="button"
          className={`toggle ${enabled ? "on" : "off"}`}
          onClick={toggleEnabled}
          aria-pressed={enabled}
        >
          <span className="dot" />
          <span className="lbl">{enabled ? "An" : "Aus"}</span>
        </button>
      </div>

      <div className={`music-grid ${enabled ? "" : "disabled"}`}>
        <div className="music-section-label">Vorlagen-Beds</div>
        <div className="music-bed-list">
          {BEDS.map((b) => (
            <button
              key={b.id}
              type="button"
              className={`music-bed ${value?.bed === b.id && !value?.src ? "active" : ""}`}
              onClick={() => setBed(b.id)}
              disabled={!enabled}
            >
              <div className="music-bed-name">{b.label}</div>
              <div className="music-bed-mood">{b.mood}</div>
            </button>
          ))}
        </div>

        <div className="music-section-label" style={{ marginTop: 14 }}>
          Eigene Library
        </div>
        <AudioLibrary
          kindFilter="all"
          defaultKind="music"
          compact
          onSelect={(asset) => useAsset(asset)}
          dragMime={null}
        />

        {value && enabled && (
          <>
            <div className="music-active-card">
              <div className="music-active-title">
                {isCustom ? "🎶" : "🎵"} {sourceLabel}
              </div>
              <div className="music-active-sub">
                {isCustom ? "Custom Upload" : "Vorlagen-Bed"} · {fromSec.toFixed(1)}s → {(fromSec + durSec).toFixed(1)}s
              </div>
            </div>

            <label className="form-row">
              <span>Lautstärke ({Math.round((value.volume ?? DEFAULT_VOLUME) * 100)}%)</span>
              <input
                type="range"
                min={0}
                max={100}
                value={Math.round((value.volume ?? DEFAULT_VOLUME) * 100)}
                onChange={(e) => patch({ volume: Number(e.target.value) / 100 })}
              />
            </label>

            <div className="form-row form-row-pair">
              <label>
                <span>Start (s)</span>
                <input
                  type="number"
                  step={0.1}
                  min={0}
                  max={totalSec}
                  value={Number(fromSec.toFixed(2))}
                  onChange={(e) =>
                    patch({ fromSec: Math.max(0, Math.min(totalSec, Number(e.target.value))) })
                  }
                />
              </label>
              <label>
                <span>Länge (s)</span>
                <input
                  type="number"
                  step={0.1}
                  min={0.2}
                  value={Number(durSec.toFixed(2))}
                  onChange={(e) => patch({ durSec: Math.max(0.2, Number(e.target.value)) })}
                />
              </label>
            </div>

            {isCustom && (
              <label className="form-row">
                <span>Trim-Start im Original ({(value.trimStartSec ?? 0).toFixed(1)}s)</span>
                <input
                  type="range"
                  min={0}
                  max={Math.max(0.1, durSec)}
                  step={0.1}
                  value={value.trimStartSec ?? 0}
                  onChange={(e) =>
                    patch({ trimStartSec: Math.max(0, Number(e.target.value)) })
                  }
                />
              </label>
            )}

            <div className="form-row form-row-pair">
              <label>
                <span>Fade-In ({(value.fadeInSec ?? 0).toFixed(1)}s)</span>
                <input
                  type="range"
                  min={0}
                  max={Math.min(8, durSec / 2)}
                  step={0.1}
                  value={value.fadeInSec ?? 0}
                  onChange={(e) =>
                    patch({ fadeInSec: Math.max(0, Number(e.target.value)) })
                  }
                />
              </label>
              <label>
                <span>Fade-Out ({(value.fadeOutSec ?? 0).toFixed(1)}s)</span>
                <input
                  type="range"
                  min={0}
                  max={Math.min(8, durSec / 2)}
                  step={0.1}
                  value={value.fadeOutSec ?? 0}
                  onChange={(e) =>
                    patch({ fadeOutSec: Math.max(0, Number(e.target.value)) })
                  }
                />
              </label>
            </div>

            <label className="form-row inline">
              <input
                type="checkbox"
                checked={value.loop ?? !isCustom}
                onChange={(e) => patch({ loop: e.target.checked })}
              />
              <span>Loop wenn Bar länger als Datei</span>
            </label>

            <div className="music-quick-actions">
              <button
                type="button"
                className="btn ghost sm"
                onClick={() => patch({ fromSec: Math.max(0, playheadSec) })}
                title="Anfang an aktuelle Playhead-Position setzen"
              >
                ↦ An Playhead
              </button>
              <button
                type="button"
                className="btn ghost sm"
                onClick={() => patch({ fromSec: 0, durSec: totalSec })}
                title="Über gesamtes Reel strecken"
              >
                ⇲ Volle Länge
              </button>
              <button type="button" className="btn ghost sm" onClick={clear}>
                Entfernen
              </button>
            </div>
          </>
        )}
      </div>

      <p className="muted" style={{ fontSize: 11.5, margin: "10px 0 0", lineHeight: 1.45 }}>
        Tipp: Du kannst Audiodateien (mp3, wav, m4a, ogg, flac …) direkt in den
        Bereich oben ziehen. Die Library ist projektübergreifend — einmal
        hochladen, in jedem Reel verwendbar.
      </p>
    </div>
  );
};
